import { useState, useEffect, useCallback, useRef } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { certificationApi } from '@/services/certification';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import {
  patientCertificationSchema,
  TPatientCertificationForm,
} from '@/pages/main/patient-certification/components/schema';
import { useCertificationSubmit } from '@/hooks/main/usePatientCertification';
import type { CopyCertificationPayload } from '@/services/certification/types';
import { transformCertificationCopyToFormData } from '@/pages/main/patient/services';
import useAppToast from '@/hooks/useAppToast';
import useAppModal from '@/hooks/useAppModal';
import { getApiErrorMessage } from '@/utils/apiError';
import { PATIENT_EDIT_HISTORY_SEARCH } from '@/pages/main/patient/utils/patientEditHistoryUrl';
import { useInsurerDialogMessages } from '@/hooks/main/useInsurerDialogMessages';

/**
 * Hook to handle certification form logic
 * This hook encapsulates all the business logic for the certification form
 */
export function useCertificationFormLogic() {
  const toast = useAppToast();
  const { warning: modalWarning } = useAppModal();
  const { getWarningDialog2Message } = useInsurerDialogMessages();
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();

  // Separate loading states for different actions
  const [isInsurerSearching, setIsInsurerSearching] = useState(false); // 保険者番号 blur search
  const [isAddressSearching, setIsAddressSearching] = useState(false); // 住所補助 search
  const [isMedicalAidSearching, setIsMedicalAidSearching] = useState(false); // 医療助成補助 search
  const [isCertificationFetching, setIsCertificationFetching] = useState(false); // 資格情報取得 detail fetch
  const [isFormSubmitting, setIsFormSubmitting] = useState(false); // 保険証画面へコピー submit

  const [insuranceType, setInsuranceType] = useState<number>(1); // 1: 一般, 2: 後期高齢

  // Modal state for duplicate patients
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicatePatientIds, setDuplicatePatientIds] = useState<number[]>([]);
  const [pendingFormData, setPendingFormData] = useState<CopyCertificationPayload | null>(null);

  // Modal state for certification list (資格情報一覧)
  const [showCertificationListModal, setShowCertificationListModal] = useState(false);

  // Skip insurer search when blur is caused by clicking submit button
  const submitButtonClickedRef = useRef(false);

  const {
    submitCertification,
    copyToExistingPatient,
    copyWithHistory,
    copyToNewPatient,
    isLoading: isSubmitting,
  } = useCertificationSubmit();

  const baseResolver = zodResolver(patientCertificationSchema);

  const form = useTrimmedForm<TPatientCertificationForm>({
    mode: 'onChange',
    resolver: async (values, context, options) => {
      const result = await baseResolver(values, context, options);

      // 記号・番号 cross-field: 他の項目にエラーがあっても常にチェックしたいのでここで追加
      const insuranceTypeId = values.insurance_type_id ?? 0;
      const hasMark = values.card_mark ? values.card_mark.trim().length > 0 : false;
      const hasNumber = values.card_number ? values.card_number.trim().length > 0 : false;

      if (insuranceTypeId !== 2 && !hasMark && !hasNumber) {
        result.errors = {
          ...result.errors,
          card_mark: {
            type: 'manual',
            message: '記号・番号のいずれか１つは入力してください。',
          },
        };
      }

      return result;
    },
    defaultValues: {
      insurer_number: '',
      relationship_id: 0,
      sex_id: 0,
      insurance_type_id: 1,
      medical_aid: {
        insurer_type_id: 1, // なし
      },
    },
  });

  const { setValue, watch, getValues, trigger } = form;

  const buildBulkHistoryRedirectUrl = useCallback(
    (redirectUrl: string, includeMedicalAidHistory: boolean): string => {
      const url = new URL(redirectUrl, 'http://localhost');
      url.searchParams.set(PATIENT_EDIT_HISTORY_SEARCH.patient_information_id, '');
      url.searchParams.set(PATIENT_EDIT_HISTORY_SEARCH.health_insurance_card_id, '');
      url.searchParams.set(PATIENT_EDIT_HISTORY_SEARCH.type, 'all_change');
      if (includeMedicalAidHistory) {
        url.searchParams.set(PATIENT_EDIT_HISTORY_SEARCH.medical_aid_id, '');
      } else {
        url.searchParams.delete(PATIENT_EDIT_HISTORY_SEARCH.medical_aid_id);
      }
      return `${url.pathname}${url.search}${url.hash}`;
    },
    []
  );

  // Watch form values
  const relationshipId = watch('relationship_id');
  const name = watch('name');
  const nameKana = watch('name_kana');

  /**
   * Convert form data to API payload format
   */
  const convertFormToPayload = useCallback(
    (data: TPatientCertificationForm): CopyCertificationPayload => {
      return {
        // Patient Information
        patient_informations_name: data.name,
        patient_informations_name_kana: data.name_kana,
        patient_informations_date_of_birth: data.date_of_birth,
        patient_informations_age: data.age,
        patient_informations_sex_id: data.sex_id,
        patient_informations_post: data.post,
        patient_informations_telephone: data.telephone,
        patient_informations_address1: data.address1,
        patient_informations_address2: data.address2,

        // Health Insurance Card
        health_insurance_cards_insurer_id: data.insurer_id ?? undefined,
        health_insurance_cards_insurer_number: data.insurer_number,
        health_insurance_cards_insurer_name: data.insurer_name,
        health_insurance_cards_insurer_kind_name: data.insurer_kind_name,
        health_insurance_cards_insurance_type_id: data.insurance_type_id,
        health_insurance_cards_relationship_id: data.relationship_id,
        health_insurance_cards_claim_type_id: data.claim_type_id,
        health_insurance_cards_card_mark: data.card_mark,
        health_insurance_cards_card_number: data.card_number,
        health_insurance_cards_card_number2: data.card_number2,
        health_insurance_cards_qualification_date: data.qualification_date,
        health_insurance_cards_expiration_date: data.expiration_date,
        health_insurance_cards_name: data.h_name,
        health_insurance_cards_name_kana: data.h_name_kana,
        health_insurance_cards_patient_cost_rate: data.patient_cost_rate,
        health_insurance_cards_cost_rate: data.cost_rate ?? undefined,
        health_insurance_cards_family_cost_rate: data.family_cost_rate ?? undefined,
        health_insurance_cards_patient_cost_id: data.patient_cost_id ?? undefined,
        health_insurance_cards_elderly_insurance_certificate_id:
          data.elderly_insurance_certificate_id ?? undefined,

        // Medical Aids
        medical_aids_insurer_type_id: data.medical_aid?.insurer_type_id,
        medical_aids_medical_aid_number: data.medical_aid?.medical_aid_number,
        medical_aids_medical_aid_patient_number: data.medical_aid?.medical_aid_patient_number,
        medical_aids_application_start_date: data.medical_aid?.application_start_date,
        medical_aids_application_end_date: data.medical_aid?.application_end_date,
      };
    },
    []
  );

  /**
   * Handle form submission - main submit button "保険証画面へコピー"
   */
  const handleSubmitForm = useCallback(
    async (data: TPatientCertificationForm) => {
      try {
        setIsFormSubmitting(true);

        const payload = convertFormToPayload(data);

        // Search for duplicate patients
        const result = await submitCertification(payload, data.sex_id, data.date_of_birth);

        if (result.hasDuplicates && result.duplicateIds) {
          setDuplicatePatientIds(result.duplicateIds);
          setPendingFormData(payload);
          setShowDuplicateModal(true);
        } else if (!result.hasDuplicates) {
          toast.success('保険証画面へコピーしました。');
          const formData = transformCertificationCopyToFormData(result.data);
          const path = result.redirectUrl ?? '/patients/edit';
          navigate(tenant ? `/${tenant}${path}` : path, {
            state: { copiedPatient: formData },
          });
        }
      } catch (error) {
        toast.error(getApiErrorMessage(error, '保険証画面へのコピーに失敗しました。'));
      } finally {
        setIsFormSubmitting(false);
      }
    },
    [convertFormToPayload, submitCertification, navigate, tenant]
  );

  /**
   * Handle selecting existing patient from duplicate modal
   */
  const handleSelectDuplicatePatient = useCallback(
    async (patientId: number) => {
      if (!pendingFormData) return;

      try {
        setIsFormSubmitting(true);
        const { redirectUrl, data } = await copyToExistingPatient(pendingFormData, patientId);

        setShowDuplicateModal(false);
        setPendingFormData(null);
        setDuplicatePatientIds([]);

        toast.success('既存の患者に保険証情報をコピーしました。');
        const formData = transformCertificationCopyToFormData(data);
        navigate(tenant ? `/${tenant}${redirectUrl}` : redirectUrl, {
          state: { copiedPatient: formData },
        });
      } catch (error) {
        toast.error(getApiErrorMessage(error, '既存の患者へのコピーに失敗しました。'));
      } finally {
        setIsFormSubmitting(false);
      }
    },
    [pendingFormData, copyToExistingPatient, navigate, tenant]
  );

  /**
   * Handle creating new patient from duplicate modal
   */
  const handleCreateNewFromDuplicate = useCallback(async () => {
    if (!pendingFormData) return;

    try {
      setIsFormSubmitting(true);
      const { redirectUrl, data } = await copyToNewPatient(pendingFormData);

      setShowDuplicateModal(false);
      setPendingFormData(null);
      setDuplicatePatientIds([]);

      toast.success('新規患者として保険証画面へコピーしました。');
      const formData = transformCertificationCopyToFormData(data);
      navigate(tenant ? `/${tenant}${redirectUrl}` : redirectUrl, {
        state: { copiedPatient: formData },
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, '新規患者の作成に失敗しました。'));
    } finally {
      setIsFormSubmitting(false);
    }
  }, [pendingFormData, copyToNewPatient, navigate, tenant]);

  /**
   * Handle copying with history creation (update_type=1)
   */
  const handleCopyWithHistory = useCallback(
    async (patientId: number) => {
      if (!pendingFormData) return;

      try {
        setIsFormSubmitting(true);
        const { redirectUrl, data } = await copyWithHistory(pendingFormData, patientId);

        setShowDuplicateModal(false);
        setPendingFormData(null);
        setDuplicatePatientIds([]);

        toast.success('保険証履歴を作成しました。');
        const formData = transformCertificationCopyToFormData(data);
        const hasMedicalAidInput =
          Number(pendingFormData.medical_aids_insurer_type_id ?? 1) !== 1 &&
          (Boolean(pendingFormData.medical_aids_medical_aid_number?.trim()) ||
            Boolean(pendingFormData.medical_aids_medical_aid_patient_number?.trim()));
        const historyRedirectUrl = buildBulkHistoryRedirectUrl(redirectUrl, hasMedicalAidInput);
        navigate(tenant ? `/${tenant}${historyRedirectUrl}` : historyRedirectUrl, {
          // BD 6.1 — flag tells patient edit page to call bulkCreateHistory on save.
          state: { copiedPatient: formData, isHistoryCreate: true },
        });
      } catch (error) {
        toast.error(getApiErrorMessage(error, '履歴の作成に失敗しました。'));
      } finally {
        setIsFormSubmitting(false);
      }
    },
    [pendingFormData, copyWithHistory, navigate, tenant, toast, buildBulkHistoryRedirectUrl]
  );

  /**
   * Close duplicate modal
   */
  const handleCloseDuplicateModal = useCallback(() => {
    setShowDuplicateModal(false);
    setPendingFormData(null);
    setDuplicatePatientIds([]);
  }, []);

  /**
   * Handle insurer number change (blur event)
   * This will fetch insurer data when the insurer number input loses focus
   */
  const handleInsurerNumberChange = useCallback(async () => {
    // Skip insurer search when user clicked submit (blur moved focus to button)
    if (submitButtonClickedRef.current) {
      submitButtonClickedRef.current = false;
      return;
    }

    const currentInsurerNumber = getValues('insurer_number');
    if (!currentInsurerNumber || currentInsurerNumber.trim().length === 0) {
      return;
    }

    try {
      setIsInsurerSearching(true);

      // Pad insurer number to 8 digits (matching RA2 behavior)
      const paddedNumber = currentInsurerNumber.trim().padStart(8, '0');
      setValue('insurer_number', paddedNumber);

      const insurerData = await certificationApi.searchInsurers(paddedNumber);

      // Update form values with insurer data
      setValue('insurer_name', insurerData.insurer_name);
      setValue('insurer_kind_name', insurerData.insurer_kind_name);
      setValue('claim_type_id', insurerData.claim_type_id);
      setValue('cost_rate', insurerData.cost_rate);
      setValue('family_cost_rate', insurerData.family_cost_rate);

      // Set the insurance type based on the insurer data
      const newInsuranceType = insurerData.insurance_type_id || 1;
      setInsuranceType(newInsuranceType);
      setValue('insurance_type_id', newInsuranceType);

      // Set relationship for 後期高齢
      if (insurerData.relationship_id) {
        setValue('relationship_id', insurerData.relationship_id);
      }

      // BD 6.1 field 3+5 — base_cost: 1/3 → cost_rate, otherwise (0/2) → family_cost_rate.
      // Reset dropdown 負担割合変更 to 初期値 (0) per BD "Reset all options".
      const currentRelationshipId = getValues('relationship_id');
      const useCostRate = currentRelationshipId === 1 || currentRelationshipId === 3;
      const baseCost = useCostRate ? insurerData.cost_rate : insurerData.family_cost_rate;
      setValue('patient_cost_rate', 0); // dropdown reset to 初期値
      setValue('futan_ritsu', String(baseCost ?? 0));

      // Clear card fields when insurer changes
      if (newInsuranceType === 2) {
        setValue('card_mark', '');
        setValue('card_number', '');
      } else {
        setValue('card_number2', '');
      }

      await trigger();
      toast.success('保険者情報を取得しました。');
    } catch {
      // BD 6.1 — when insurer not found:
      // 1. Clear ONLY 保険者番号 (other fields incl. 保険者名 keep their previous values)
      // 2. Show site_type-aware warning dialog
      setValue('insurer_number', '');

      const dialogContent = getWarningDialog2Message().message;

      modalWarning({
        title: '保険者番号エラー',
        content: dialogContent,
        okText: 'OK',
      });
    } finally {
      setIsInsurerSearching(false);
    }
  }, [setValue, getValues, trigger, toast, modalWarning, getWarningDialog2Message]);

  /**
   * BD 6.1 mapping for 負担割合変更 dropdown → 受診者負担 (futan_ritsu).
   * `null` value means "use base_cost from insurer master".
   */
  const PATIENT_COST_DROPDOWN_TO_RATE_KOUKI: Record<number, number | null> = {
    0: null, // 初期値 → base_cost
    1: 1,
    2: 2,
    3: 3,
  };
  const PATIENT_COST_DROPDOWN_TO_RATE_PUBLIC: Record<number, number | null> = {
    0: null, // 初期値 → base_cost
    1: 2, // 未就学児２割
    2: 10, // 10割負担
    3: 0, // 0割負担
    4: 1, // 前期高齢１割
    5: 2, // 前期高齢２割
    6: 3, // 前期高齢３割
  };

  /**
   * BD 6.1 — base_cost depends on 続柄.
   * 1 (本人) / 3 (67本人) → cost_rate; otherwise → family_cost_rate.
   */
  const computeBaseCost = useCallback(
    (relId: number): number => {
      const costRate = getValues('cost_rate') ?? 0;
      const familyCostRate = getValues('family_cost_rate') ?? 0;
      return relId === 1 || relId === 3 ? Number(costRate) : Number(familyCostRate);
    },
    [getValues]
  );

  /**
   * Handle 負担割合変更 dropdown change → update 受診者負担 (futan_ritsu).
   * BD 6.1 field 4: dropdown value has highest priority over 続柄/保険者番号.
   */
  const handleCostRateDropdownChange = useCallback(
    (newValue: number) => {
      const mapping =
        insuranceType === 2
          ? PATIENT_COST_DROPDOWN_TO_RATE_KOUKI
          : PATIENT_COST_DROPDOWN_TO_RATE_PUBLIC;
      const mapped = mapping[newValue];

      if (mapped === null || mapped === undefined) {
        // 初期値 → use base_cost (per 続柄)
        const relId = getValues('relationship_id') ?? 0;
        const base = computeBaseCost(relId);
        setValue('futan_ritsu', String(base));
      } else {
        setValue('futan_ritsu', String(mapped));
      }

      // Persist patient_cost_id / elderly_insurance_certificate_id per legacy mapping (PUBLIC only).
      if (insuranceType !== 2) {
        if (newValue === 0) {
          setValue('patient_cost_id', 0);
          setValue('elderly_insurance_certificate_id', undefined);
        } else if (newValue >= 1 && newValue <= 3) {
          setValue('patient_cost_id', newValue);
          setValue('elderly_insurance_certificate_id', undefined);
        } else if (newValue >= 4 && newValue <= 6) {
          setValue('patient_cost_id', undefined);
          setValue('elderly_insurance_certificate_id', newValue - 2);
        }
      }
    },
    [
      insuranceType,
      getValues,
      setValue,
      computeBaseCost,
      PATIENT_COST_DROPDOWN_TO_RATE_KOUKI,
      PATIENT_COST_DROPDOWN_TO_RATE_PUBLIC,
    ]
  );

  /**
   * Handle 続柄 change.
   * BD 6.1 field 5:
   * - Copy 受診者氏名漢字/仮名 → 被保険者名/仮名 with rule:
   *   1 (本人) / 3 (67本人) / 0 (empty) → full name
   *   2 (家族) → 姓 only (split by space, take first part)
   * - Update 受診者負担 only when 負担割合変更 = 0 (初期値).
   */
  const handleRelationshipChange = useCallback(
    (value: number) => {
      const fullName = name ?? '';
      const fullNameKana = nameKana ?? '';

      if (value === 2) {
        // 家族 → take 姓 only (split by half/full-width space, take first)
        const surname = fullName.split(/[\s　]+/)[0] ?? '';
        const surnameKana = fullNameKana.split(/[\s　]+/)[0] ?? '';
        setValue('h_name', surname);
        setValue('h_name_kana', surnameKana);
      } else {
        // 1 / 3 / 0 → full name
        setValue('h_name', fullName);
        setValue('h_name_kana', fullNameKana);
      }

      // Update 受診者負担 only when dropdown is on 初期値 (0) AND insurer data loaded.
      // Prevents futan_ritsu='0' on initial mount before any 保険者番号 lookup.
      const dropdownValue = getValues('patient_cost_rate') ?? 0;
      if (dropdownValue === 0) {
        const costRate = getValues('cost_rate');
        const familyCostRate = getValues('family_cost_rate');
        if (costRate != null || familyCostRate != null) {
          const base = computeBaseCost(value);
          setValue('futan_ritsu', String(base));
        }
      }
    },
    [name, nameKana, setValue, getValues, computeBaseCost]
  );

  /**
   * BD 6.1 field 21/23: When 受診者氏名(漢字/仮名) changes, auto-copy to 被保険者名(/仮名)
   * per 続柄 rule (1/3/0 = full, 2 = surname only).
   * This runs for ALL relationship values (including 0, 2) — not only 1/3.
   */
  useEffect(() => {
    handleRelationshipChange(Number(relationshipId ?? 0));
  }, [relationshipId, name, nameKana, handleRelationshipChange]);

  /**
   * Handle search address
   * This will fetch address data based on postal code
   */
  const handleSearchAddress = useCallback(async () => {
    const post = watch('post');
    if (!post || post.length < 3) {
      toast.warning('郵便番号を入力してください。');
      return;
    }

    try {
      setIsAddressSearching(true);
      const addressData = await certificationApi.searchAddresses(post);

      // Update address fields
      setValue('address1', addressData.address1 || '');
      setValue('address2', addressData.address2 || '');

      toast.success('住所を取得しました。');
    } catch (error) {
      toast.error(getApiErrorMessage(error, '住所の取得に失敗しました。'));
    } finally {
      setIsAddressSearching(false);
    }
  }, [watch, setValue]);

  /**
   * Handle search medical aid
   * This will fetch medical aid information
   */
  const handleSearchMedicalAid = useCallback(async () => {
    const insurerTypeId = watch('medical_aid.insurer_type_id');
    const medicalAidNumber = watch('medical_aid.medical_aid_number');

    if (!insurerTypeId || !medicalAidNumber) {
      toast.warning('公費負担者番号を入力してください。');
      return;
    }

    try {
      setIsMedicalAidSearching(true);
      const medicalAidData = await certificationApi.searchMedicalAids(
        Number(insurerTypeId),
        String(medicalAidNumber)
      );

      // Update medical aid fields
      setValue('medical_aid.claim_type_id', medicalAidData.claim_type_id);
      setValue('medical_aid.calc_type', medicalAidData.calc_type);
      setValue('medical_aid.limit_amount', medicalAidData.limit_amount);
      setValue('medical_aid.limit_num_month', medicalAidData.limit_num_month);
      setValue('medical_aid.limit_amount_month', medicalAidData.limit_amount_month);
      setValue('medical_aid.insurer_id', medicalAidData.insurer_id);
      setValue('medical_aid.insurer_cost_rate', String(medicalAidData.insurer_cost_rate ?? ''));
      setValue('medical_aid.cost_rate', medicalAidData.cost_rate);
      setValue('medical_aid.family_cost_rate', medicalAidData.family_cost_rate);
      setValue('medical_aid.is_partial_aid', medicalAidData.is_partial_aid);
      setValue('medical_aid.is_nonoutput_rezept', medicalAidData.is_nonoutput_rezept);

      // Set cost rate based on relationship
      const costRateField = relationshipId === 2 ? 'family_cost_rate' : 'cost_rate';
      const costRateValue = medicalAidData[costRateField];

      setValue('medical_aid.insurer_cost_rate', costRateValue != null ? String(costRateValue) : '');

      toast.success('公費負担情報を取得しました。');
    } catch (error) {
      toast.error(getApiErrorMessage(error, '公費負担情報の取得に失敗しました。'));
    } finally {
      setIsMedicalAidSearching(false);
    }
  }, [watch, setValue, relationshipId]);

  /**
   * Handle get certification - opens the certification list modal (資格情報一覧)
   */
  const handleGetCertification = useCallback(() => {
    setShowCertificationListModal(true);
  }, []);

  /**
   * Handle close certification list modal
   */
  const handleCloseCertificationListModal = useCallback(() => {
    setShowCertificationListModal(false);
  }, []);

  /**
   * Handle select certification from list modal
   * Fetches certification detail and fills ALL form fields
   */
  const handleSelectCertification = useCallback(
    async (certificationId: number) => {
      try {
        setIsCertificationFetching(true);
        setShowCertificationListModal(false);

        const certificationData = await certificationApi.getCertificationDetail(certificationId);

        // Fill patient information
        if (certificationData.patient_informations_data) {
          const pi = certificationData.patient_informations_data;
          setValue('name', pi.name || '');
          setValue('name_kana', pi.name_kana || '');
          setValue('date_of_birth', pi.date_of_birth || '');
          setValue('age', pi.age ?? undefined);
          setValue('sex_id', pi.sex_id || 0);
        }

        // Fill health insurance card information
        if (certificationData.health_insurance_cards_data) {
          const hic = certificationData.health_insurance_cards_data;

          // Insurance type and insurer info
          const newInsuranceType = hic.insurance_type_id || 1;
          setValue('insurance_type_id', newInsuranceType);
          setInsuranceType(newInsuranceType);
          setValue('insurer_number', hic.insurer_number || '');
          setValue('insurer_id', hic.insurer_id ?? undefined);
          setValue('insurer_name', hic.insurer_name || '');
          setValue('insurer_kind_name', hic.insurer_kind_name || '');
          setValue('claim_type_id', hic.claim_type_id ?? undefined);

          // Relationship
          setValue('relationship_id', hic.relationship_id ?? 0);

          // Insured person name
          setValue('h_name', hic.name || '');
          setValue('h_name_kana', hic.name_kana || '');

          // Card mark/number
          setValue('card_mark', hic.card_mark || '');
          setValue('card_number', hic.card_number || '');
          if (newInsuranceType === 2) {
            setValue('card_number2', hic.card_number || '');
          }

          // Dates
          setValue('qualification_date', hic.qualification_date || '');
          setValue('expiration_date', hic.expiration_date || '');

          // Cost rates
          setValue('patient_cost_rate', 0); // dropdown always reset to 初期値
          setValue('cost_rate', hic.cost_rate ?? undefined);
          setValue('family_cost_rate', hic.family_cost_rate ?? undefined);
          setValue('patient_cost_id', hic.patient_cost_id ?? undefined);
          setValue(
            'elderly_insurance_certificate_id',
            hic.elderly_insurance_certificate_id ?? undefined
          );

          // Set display cost rate (受診者負担)
          if (hic.patient_cost_rate != null) {
            setValue('futan_ritsu', String(hic.patient_cost_rate));
          }
        }

        // Re-validate all fields to clear stale errors
        await trigger();

        toast.success('資格情報を反映しました。');
      } catch (error) {
        toast.error(getApiErrorMessage(error, '資格情報の取得に失敗しました。'));
      } finally {
        setIsCertificationFetching(false);
      }
    },
    [setValue, toast, trigger]
  );

  // Combined loading state for overlay (any background operation)
  const isOverlayLoading = isInsurerSearching || isCertificationFetching;

  const onSubmitButtonMouseDown = useCallback(() => {
    submitButtonClickedRef.current = true;
  }, []);

  return {
    form,
    insuranceType,
    // Separate loading states for specific actions
    isInsurerSearching, // 保険者番号 blur - shows overlay only
    isAddressSearching, // 住所補助 button
    isMedicalAidSearching, // 医療助成補助 button
    isCertificationFetching, // 資格情報取得 detail fetch - shows overlay
    isFormSubmitting: isFormSubmitting || isSubmitting, // 保険証画面へコピー button
    isOverlayLoading, // For full-page overlay
    // Form handlers
    handleInsurerNumberChange,
    onSubmitButtonMouseDown,
    handleRelationshipChange,
    handleCostRateDropdownChange,
    handleSearchAddress,
    handleSearchMedicalAid,
    handleGetCertification,
    handleSubmitForm,
    // Duplicate modal state and handlers
    showDuplicateModal,
    duplicatePatientIds,
    handleSelectDuplicatePatient,
    handleCreateNewFromDuplicate,
    handleCopyWithHistory,
    handleCloseDuplicateModal,
    // Certification list modal state and handlers (資格情報一覧)
    showCertificationListModal,
    handleCloseCertificationListModal,
    handleSelectCertification,
    // Form data for modal preview and comparison API
    getFormDataForComparison: () => ({
      name: getValues('name') ?? '',
      card_mark: getValues('card_mark') ?? '',
      card_number:
        insuranceType === 2 ? (getValues('card_number2') ?? '') : (getValues('card_number') ?? ''),
      insurer_number: getValues('insurer_number') ?? '',
      qualification_date: getValues('qualification_date') ?? '',
      expiration_date: getValues('expiration_date') ?? '',
      address1: getValues('address1') ?? '',
      address2: getValues('address2') ?? '',
      // Send the displayed rate (futan_ritsu), not the dropdown override code
      // (patient_cost_rate). The DB stores the actual rate, so comparing the
      // code against it would always diff — e.g. 前期高齢３割 (code 6) vs stored 3.
      patient_cost_rate: String(getValues('futan_ritsu') ?? ''),
    }),
  };
}
